import { useMemo } from "react";
import cn from "@/lib/cn";
import { PatientAvatar } from "./PatientAvatar";
import { PatientMeta } from "./PatientMeta";
import { CardStatusBadge } from "./CardStatusBadge";
import { PaymentCardShortInfo } from "@/features/payment/types";
import {
  CommandPalettePatient,
  CommandPaletteResult,
} from "@/components/command-palette/types";
import { useCommandPaletteStore } from "@/components/command-palette/commandPaletteStore";

interface PatientResultItemProps {
  patient: CommandPalettePatient;
  cardInfo: PaymentCardShortInfo | null;
  onSelect: VoidFunction;
  flatResults: CommandPaletteResult[];
}

export function PatientResultItem({
  patient,
  flatResults,
  cardInfo,
  onSelect,
}: PatientResultItemProps) {
  const flatIndex = useMemo(
    () =>
      flatResults.findIndex((r) => r.patient?.patientId === patient.patientId),
    [flatResults, patient.patientId],
  );

  const isSelected = useCommandPaletteStore(
    (s) => s.selectedIndex === flatIndex,
  );
  const setSelectedIndex = useCommandPaletteStore((s) => s.setSelectedIndex);
  const fullName = `${patient.firstName} ${patient.lastName}`;

  return (
    <button
      data-selected={isSelected}
      onClick={onSelect}
      onMouseEnter={() => setSelectedIndex(flatIndex)}
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 border-0 px-5 py-2 text-left transition-colors duration-100",
        isSelected ? "bg-(--brand-gray)" : "bg-transparent",
      )}
    >
      <PatientAvatar fullName={fullName} />

      <div className="flex flex-1 flex-col gap-px">
        <span className="overflow-hidden text-[13px] font-semibold text-ellipsis whitespace-nowrap text-[#111827]">
          {fullName}
        </span>
        <PatientMeta patient={patient} />
      </div>

      <CardStatusBadge cardInfo={cardInfo} />
    </button>
  );
}
